import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';

import { useLanguage } from '../../../context/LanguageContext';
import { useThemeContext } from '../../../context/ThemeContext';
import { useGetLanguages } from '../../../redux/api/translationsAPI';
import {
  useGetMyDetailsApiQuery,
  usePutRegistrationCompletion,
  useUpdateUser,
} from '../../../redux/api/users/usersApi';
import { Paths } from '../../../shared/paths';
import { IUser } from '../../../types/interfaces/user';
import { getRegistrationStepValidation } from './FormikData';
import {
  buildRegistrationCompletionBody,
  buildRegistrationUserUpdateBody,
  getDefaultWizardValues,
} from './build-registration-payload';
import {
  IRegistrationWizardValues,
  REGISTRATION_WIZARD_STEPS,
  RegistrationWizardStep,
} from './complete-registration.types';

function getStepFieldNames(step: RegistrationWizardStep): string[] {
  switch (step) {
    case RegistrationWizardStep.name:
      return ['firstname', 'lastname'];
    case RegistrationWizardStep.measurements:
      return ['weightValue', 'heightValue'];
    case RegistrationWizardStep.visualization:
      return ['language'];
    default:
      return [];
  }
}

export function useCompleteRegistrationWizard(
  me: IUser | undefined,
  refetchMe: () => Promise<unknown>,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const { data: languages } = useGetLanguages();
  const { theme, primaryColor, applyVisualizationConfig } = useThemeContext();
  const [putRegistrationCompletion, { isLoading: isSavingRegistration }] =
    usePutRegistrationCompletion();
  const [updateUser, { isLoading: isSavingUser }] = useUpdateUser();
  const [step, setStep] = useState<RegistrationWizardStep>(
    RegistrationWizardStep.name,
  );
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);

  const didInitializeForm = useRef(false);

  const formik = useFormik<IRegistrationWizardValues>({
    enableReinitialize: false,
    initialValues: getDefaultWizardValues({
      firstname: '',
      lastname: '',
      theme,
      primaryColor,
      language: undefined,
    }),
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (didInitializeForm.current || !me?.id || !languages?.length) {
      return;
    }

    formik.resetForm({
      values: getDefaultWizardValues({
        firstname: me.firstname,
        lastname: me.lastname,
        theme,
        primaryColor,
        language:
          languages.find(
            (language) =>
              language.id === (me.languageId || currentLanguage?.id),
          ) ?? languages[0],
        accessConfig: me.accessConfig,
      }),
    });
    didInitializeForm.current = true;
  }, [
    currentLanguage?.id,
    formik,
    languages,
    me?.firstname,
    me?.id,
    me?.languageId,
    me?.lastname,
    me?.accessConfig,
    primaryColor,
    theme,
  ]);

  const stepIndex = REGISTRATION_WIZARD_STEPS.indexOf(step);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === REGISTRATION_WIZARD_STEPS.length - 1;
  const isSaving = isSavingRegistration || isSavingUser;

  const validateCurrentStep = useCallback(async () => {
    const schema = getRegistrationStepValidation(step, t);
    const fieldNames = getStepFieldNames(step);
    const values = fieldNames.reduce<Record<string, unknown>>((acc, name) => {
      acc[name] = formik.values[name as keyof IRegistrationWizardValues];
      return acc;
    }, {});

    try {
      await schema.validate(values, { abortEarly: false });
      return true;
    } catch (error) {
      const touched = fieldNames.reduce<Record<string, boolean>>(
        (acc, name) => {
          acc[name] = true;
          return acc;
        },
        {},
      );
      formik.setTouched({ ...formik.touched, ...touched }, false);

      if (error && typeof error === 'object' && 'inner' in error) {
        const validationError = error as {
          inner: Array<{ path?: string; message: string }>;
        };
        const errors = validationError.inner.reduce<Record<string, string>>(
          (acc, item) => {
            if (item.path) {
              acc[item.path] = item.message;
            }
            return acc;
          },
          {},
        );
        formik.setErrors({ ...formik.errors, ...errors });
      }

      return false;
    }
  }, [formik, step, t]);

  const submitRegistration = useCallback(
    async (includeConfig: boolean) => {
      const body = buildRegistrationCompletionBody(formik.values, {
        includeConfig,
      });

      await putRegistrationCompletion(body).unwrap();
      setCompletionDialogOpen(true);

      if (includeConfig && me?.id && formik.values.language?.id) {
        await updateUser({
          id: me.id,
          body: buildRegistrationUserUpdateBody(
            formik.values,
            formik.values.language,
          ),
        }).unwrap();

        applyVisualizationConfig({
          theme: formik.values.theme,
          primaryColor: formik.values.primaryColor,
        });
      }

      await refetchMe();
    },
    [
      applyVisualizationConfig,
      formik.values,
      me?.id,
      putRegistrationCompletion,
      refetchMe,
      updateUser,
    ],
  );

  const goToNextStep = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    const nextStep = REGISTRATION_WIZARD_STEPS[stepIndex + 1];
    if (nextStep) {
      setStep(nextStep);
    }
  }, [stepIndex, validateCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = REGISTRATION_WIZARD_STEPS[stepIndex - 1];
    if (previousStep) {
      setStep(previousStep);
    }
  }, [stepIndex]);

  const handleSkipSetupClick = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    try {
      await submitRegistration(false);
    } catch {
      void 0;
    }
  }, [submitRegistration, validateCurrentStep]);

  const handleSave = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    try {
      await submitRegistration(true);
    } catch {
      void 0;
    }
  }, [submitRegistration, validateCurrentStep]);

  const handleCompletionDialogConfirm = useCallback(() => {
    navigate(Paths.root, { replace: true });
  }, [navigate]);

  return {
    formik,
    step,
    isFirstStep,
    isLastStep,
    isSaving,
    completionDialogOpen,
    goToNextStep,
    goToPreviousStep,
    handleSkipSetupClick,
    handleSave,
    handleCompletionDialogConfirm,
    languages,
  };
}
